import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Locale, MessagesShape, Script } from './types.js';
import { enUS, zhCN } from './locales.js';

export interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (path: string, vars?: Record<string, string | number>) => string;
  script: (text: string) => Script;
  textClass: (text: string) => string;
  format: {
    number: (n: number) => string;
    date: (d: Date | number) => string;
    relativeTime: (deltaMs: number) => string;
  };
}

const DEFAULT_LOCALES: Record<Locale, MessagesShape> = {
  'zh-CN': zhCN,
  'en-US': enUS,
};

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  children: ReactNode;
  defaultLocale?: Locale;
  locales?: Partial<Record<Locale, MessagesShape>>;
  fallback?: Locale;
  storageKey?: string;
}

function detectLocale(): Locale {
  if (typeof document !== 'undefined') {
    const lang = document.documentElement.lang.toLowerCase();
    if (lang.startsWith('zh')) return 'zh-CN';
    if (lang.startsWith('en')) return 'en-US';
  }
  if (typeof navigator === 'undefined') return 'en-US';
  const langs: readonly string[] =
    navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  for (const l of langs) {
    if (l.toLowerCase().startsWith('zh')) return 'zh-CN';
    if (l.toLowerCase().startsWith('en')) return 'en-US';
  }
  return 'en-US';
}

export function I18nProvider(props: I18nProviderProps): ReactNode {
  const storageKey = props.storageKey ?? 'viquard.locale';
  const locales = { ...DEFAULT_LOCALES, ...props.locales };
  const fallback = props.fallback ?? 'en-US';

  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored !== null && stored in locales) return stored as Locale;
      } catch {
        /* ignore */
      }
    }
    return props.defaultLocale ?? detectLocale();
  });

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', locale);
    }
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(storageKey, locale);
      } catch {
        /* ignore */
      }
    }
  }, [locale, storageKey]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
  }, []);

  const t = useCallback(
    (path: string, vars?: Record<string, string | number>) => {
      const parts = path.split('.');
      const pick = (src: MessagesShape): string | undefined => {
        let cur: unknown = src;
        for (const p of parts) {
          if (cur !== null && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
            cur = (cur as Record<string, unknown>)[p];
          } else {
            return undefined;
          }
        }
        return typeof cur === 'string' ? cur : undefined;
      };
      const fallbackMsgs = locales[fallback];
      const msgs = locales[locale];
      let raw = pick(msgs) ?? pick(fallbackMsgs) ?? path;
      if (vars !== undefined) {
        for (const key in vars) {
          raw = raw.replace(new RegExp(`\\{${key}\\}`, 'g'), String(vars[key]));
        }
      }
      return raw;
    },
    [locale, fallback, locales],
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t,
      script: scriptOf,
      textClass: (text: string) => {
        const s = scriptOf(text);
        if (s === 'cjk') return 'vq-text-cjk';
        if (s === 'latin') return 'vq-text-en';
        return 'vq-text-mixed';
      },
      format: {
        number: (n) => new Intl.NumberFormat(locale).format(n),
        date: (d) => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(d)),
        relativeTime: (deltaMs) => {
          const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
          const absMs = Math.abs(deltaMs);
          if (absMs < 60_000) return rtf.format(Math.round(deltaMs / 1000), 'second');
          if (absMs < 3_600_000) return rtf.format(Math.round(deltaMs / 60_000), 'minute');
          if (absMs < 86_400_000) return rtf.format(Math.round(deltaMs / 3_600_000), 'hour');
          return rtf.format(Math.round(deltaMs / 86_400_000), 'day');
        },
      },
    }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Safe i18n `t()` hook for library primitives.
 *  - Inside `<I18nProvider>`: uses the provider's resolved locale + messages.
 *  - Outside the provider: falls back to `navigator.language` → zh-CN/en-US
 *    built-in locales, so a consumer who forgets the provider still gets
 *    the user's preferred language instead of a raw key. */
export function useI18nT(): (path: string, vars?: Record<string, string | number>) => string {
  const ctx = useContext(I18nContext);
  if (ctx !== null) return ctx.t;
  // Standalone fallback: pick locale from navigator, resolve against static tables.
  const fallbackLocale: Locale = detectLocale();
  const msgs: MessagesShape = fallbackLocale === 'zh-CN' ? zhCN : enUS;
  return (path, vars) => {
    const parts = path.split('.');
    let cur: unknown = msgs;
    for (const p of parts) {
      if (cur !== null && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
        cur = (cur as Record<string, unknown>)[p];
      } else {
        return path;
      }
    }
    let raw = typeof cur === 'string' ? cur : path;
    if (vars !== undefined) {
      for (const key in vars) {
        raw = raw.replace(new RegExp(`\\{${key}\\}`, 'g'), String(vars[key]));
      }
    }
    return raw;
  };
}

function scriptOf(text: string): Script {
  let hasCjk = false;
  let hasLatin = false;
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (
      (code >= 0x4e00 && code <= 0x9fff) ||
      (code >= 0x3040 && code <= 0x30ff) ||
      (code >= 0xac00 && code <= 0xd7af)
    ) {
      hasCjk = true;
    } else if ((code >= 0x41 && code <= 0x7a) || (code >= 0xc0 && code <= 0x2af)) {
      hasLatin = true;
    }
    if (hasCjk && hasLatin) return 'mixed';
  }
  if (hasCjk) return 'cjk';
  return 'latin';
}

export interface BilingualLabelProps {
  zh: string;
  en: string;
  separator?: string;
}

export function BilingualLabel(props: BilingualLabelProps): ReactNode {
  const { locale } = useI18n();
  const sep = props.separator ?? ' · ';
  const primary = locale === 'zh-CN' ? props.zh : props.en;
  const secondary = locale === 'zh-CN' ? props.en : props.zh;
  return (
    <span className="vq-bilingual-label">
      <span>{primary}</span>
      <span className="vq-bilingual-label__sep" aria-hidden="true">
        {sep}
      </span>
      <span className="vq-bilingual-label__secondary">{secondary}</span>
    </span>
  );
}
